// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * The one HTTP client for the ReUseX GUI API (`docs/gui/openapi.yaml`, v1.0.0).
 *
 * Deliberately a single module: every assumption this frontend makes about the
 * contract lives here, so contract polish (#285) and the binary point transport
 * (#283) each have exactly one file to change.
 *
 * Two rules from `docs/gui/README.md` are baked in and must not be worked
 * around in calling code:
 *
 *  0. **Case-scoped.** Every project route lives under
 *     `/api/v1/cases/{cid}` (ruxd multi-case spec, phase S2). The app's `api`
 *     is pointed at one case with {@link RuxApiClient.selectCase} before the
 *     first render (`main.tsx`); the few server-level routes (the SAM3 model
 *     status) resolve against `serverBaseUrl` instead. The case list itself
 *     lives in `cases.ts`.
 *  1. **Same-origin only.** The default base URL is a relative `/api/v1`. In
 *     development, Vite's `server.proxy` forwards it to `ruxd --local`, so the
 *     browser never makes a cross-origin request. `ruxd --local` cannot answer a
 *     CORS preflight (Crow 1.3 replies to `OPTIONS` before it has parsed the
 *     request headers), so any JSON-bodied cross-origin call would fail.
 *  2. **Mutating routes carry `Content-Type: application/json`.** The server
 *     requires it — it is what a forged "simple request" cannot set.
 */

import { authAwareFetch, authRetry } from '../app/unauthorized';
import { ERROR_CODES } from './types';
import type {
  BuildingElement,
  CloudInfo,
  CloudPointsPage,
  CloudTileIndex,
  ComponentDetail,
  ComponentInfo,
  DescriptorMatchResult,
  DescriptorMethod,
  EndpointInfo,
  ErrorCode,
  FrameInfo,
  FrameImageKind,
  FrameList,
  FrameSegmentRequest,
  FrameSegmentResult,
  FrameVisibilityList,
  GsplatInfo,
  Health,
  IfcImportOptions,
  IfcUpload,
  RtabmapCompleteOptions,
  BlockSuggestions,
  BlockSuggestQuery,
  BlockTransformRequest,
  BlockTransformResult,
  DetachResult,
  PoseBlockInput,
  PoseBlockList,
  PoseEditsDocument,
  PoseVersionChange,
  PoseVersionDiff,
  PoseVersionList,
  RebuildOdometryResult,
  InstanceInfo,
  InstancePanoramaList,
  Job,
  ModelEditRequest,
  ModelEditResult,
  ModelHistory,
  ModelRevision,
  ModelSnap,
  ModelUndoRequest,
  JobChainRequest,
  JobRequest,
  LabelLegend,
  MaterialCreate,
  MaterialDetail,
  MaterialInfo,
  MeshInfo,
  PanoramaInfo,
  PanoramaPlacementPatch,
  PanoramaUploadQuery,
  PanoramaSegmentRequest,
  PanoramaSegmentResult,
  Sam3ModelStatus,
  SpeckleImportRequest,
  SpeckleTokenPut,
  SpeckleTokenStatus,
  SolidOpening,
  SolidSlab,
  SolidSpace,
  SpacesMergeRequest,
  SpacesMergeResult,
  SpacesRebuildRequest,
  SpacesRebuildResult,
  SpacesSplitRequest,
  SpacesSplitResult,
  CeilingFitRequest,
  CeilingFitResult,
  Stair,
  Zone,
  SolidWall,
  SegmentResourceRequest,
  SegmentResourceResult,
  PipelineLogEntry,
  IcpRefineRequest,
  IcpRefineResult,
  PoseGraph,
  PoseGraphEdge,
  PoseGraphEdgeCreate,
  PoseGraphEdgeDeleteResult,
  PoseGraphEdgeType,
  ProjectInfo,
  ProjectSummary,
  PropertyDefinition,
  RenderQuery,
  ReportCover,
  ReportPdfVersion,
  ReportPreview,
  Resource,
  ResourceColumnCreate,
  ResourceCreate,
  ResourceKey,
  ResourcePatchResult,
  ResourceView,
  Sample,
  SampleCreate,
  SampleLinksPut,
  SamplePatch,
  StageInfo,
  Template,
  TemplateCreate,
  TemplatePatch,
  Survey,
  SurveyFractions,
  SurveyPart,
  SurveyPartPatch,
  SurveyTypeBatchPatch,
  SurveyTypeMergeResult,
  Storey,
  Room,
  SurveyPhotos,
  SurveySummary,
  SurveySyncReport,
  SurveyMeasureReport,
  Buildup,
  SurveyType,
  SurveyTypeCreate,
  SurveyTypeDeletion,
  SurveyTypePatch,
  TextureInfo,
  VisibleFrame,
} from './types';

/** Default base path. Relative on purpose — see rule 1 above. */
export const DEFAULT_BASE_URL = '/api/v1';

/** The base URL of one case's routes: `/api/v1/cases/{cid}`. */
export function caseBaseUrl(cid: string, serverBaseUrl: string = DEFAULT_BASE_URL): string {
  return `${serverBaseUrl.replace(/\/+$/, '')}/cases/${encodeURIComponent(cid)}`;
}

/**
 * A non-2xx response, carrying the status so a caller can branch on it.
 *
 * The statuses worth branching on, per the contract: `404` (no such resource),
 * `501` (`format=binary` against a server that predates #283 — fall back to
 * JSON), `503` (the project database was momentarily locked by a running job —
 * retry), `403` (origin not allowlisted) and `415` (mutating route without the
 * JSON content type).
 *
 * `code` (ruxd-202) is the stable, machine-readable counterpart of `message`:
 * branch on it first, and keep the status/message switch only as a fallback
 * for a server that predates it (`frontend-78b`). It is `undefined` when the
 * server sent none, or sent a string this build does not recognise —
 * {@link isErrorCode} treats an unknown code exactly like a missing one, per
 * the contract.
 */
export class ApiRequestError extends Error {
  readonly status: number;
  readonly url: string;
  readonly code?: ErrorCode;

  constructor(status: number, message: string, url: string, code?: ErrorCode) {
    super(message);
    this.name = 'ApiRequestError';
    this.status = status;
    this.url = url;
    this.code = code;
  }

  /** The resource does not exist. */
  get isNotFound(): boolean {
    return this.status === 404;
  }

  /** The server understands the request but has not implemented it yet. */
  get isNotImplemented(): boolean {
    return this.status === 501;
  }

  /** Transient: the database was locked by a running job. Safe to retry. */
  get isRetryable(): boolean {
    return this.status === 503;
  }

  /** The server refused a well-formed request on a rule, e.g. approving while a sample is pending. */
  get isUnprocessable(): boolean {
    return this.status === 422;
  }

  /**
   * The edit conflicts with the project's state and was **not** applied.
   *
   * Deliberately distinct from {@link isRetryable}: 503 means "the writer lock
   * was busy for a moment, send it again", while 409 means either a pipeline
   * job holds the lock for the duration of a stage — retrying now will fail
   * again — or the server refuses this edit outright and always will. Telling
   * the user to "try again" for a 409 is advice that cannot work.
   */
  get isConflict(): boolean {
    return this.status === 409;
  }
}

/** Injectable `fetch`, so tests need no network and no global patching. */
export type FetchLike = (
  input: string,
  init?: { method?: string; headers?: Record<string, string>; body?: string | Blob; signal?: AbortSignal },
) => Promise<Response>;

export interface ClientOptions {
  /**
   * Base URL of the project routes: a case's `/api/v1/cases/{cid}`
   * ({@link caseBaseUrl}). Defaults to `/api/v1`, which only the tests use.
   */
  baseUrl?: string;
  /** Base URL of the server-level routes. Defaults to `/api/v1`. */
  serverBaseUrl?: string;
  /** Override the transport. Defaults to the global `fetch`. */
  fetch?: FetchLike;
}

/** Query parameters, with `undefined` entries dropped rather than serialised. */
type Query = Record<string, string | number | boolean | undefined>;

function buildQuery(query?: Query): string {
  if (!query) return '';
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined) params.set(key, String(value));
  }
  const encoded = params.toString();
  return encoded ? `?${encoded}` : '';
}

/**
 * How much of a cloud to ask for, in one of the endpoint's two modes.
 *
 * `offset`/`limit` take a **window** of the cloud in storage order.
 * `maxPoints` takes a spatially representative view of **all** of it (#320) —
 * a different question, so the server refuses the two together rather than
 * inventing a meaning for an offset into a set the client cannot enumerate.
 * That mutual exclusion is enforced here too, one layer earlier, so a caller
 * gets a `TypeError` at the call site instead of a 400 over the wire.
 */
export interface CloudPointsQuery {
  offset?: number;
  limit?: number;
  /**
   * Budget for a level-of-detail read of the whole cloud. The answer holds at
   * most this many points, and — because the underlying grid is dyadic — may
   * hold as few as roughly a quarter of them.
   */
  maxPoints?: number;
  /**
   * Cloud whose positions drive the selection, for a sibling that has none of
   * its own (a `Label` cloud). The two must be index-aligned; the resulting
   * pages then describe the same points and can still be zipped positionally.
   */
  lodSource?: string;
  /** Tile ID for spatially-partitioned streaming (#395). */
  tile?: number;
}

function pointsQuery(options: CloudPointsQuery): Query {
  if (options.maxPoints !== undefined && (options.offset !== undefined || options.limit !== undefined)) {
    throw new TypeError('maxPoints cannot be combined with offset or limit');
  }
  return {
    offset: options.offset,
    limit: options.limit,
    max_points: options.maxPoints,
    lod_source: options.lodSource,
    tile: options.tile,
  };
}

/** Whether `value` is one of the codes `ErrorCode` lists — the runtime half of the contract's open-ended enum. */
export function isErrorCode(value: unknown): value is ErrorCode {
  return typeof value === 'string' && (ERROR_CODES as readonly string[]).includes(value);
}

/** {@link describeFailure}'s message, plus the `code` field when the body carries a recognised one. */
export interface FailureDetail {
  message: string;
  code?: ErrorCode;
}

/**
 * Pull the reason — and, when present, the stable {@link ErrorCode} — out of
 * a failed response.
 *
 * The contract says every non-2xx body is an `Error` object with a non-empty
 * `error` and, often, a `code`. A server that is wedged badly enough may not
 * manage even that, so this degrades to the status text rather than throwing
 * while building a throw.
 */
export async function describeFailureDetailed(response: Response): Promise<FailureDetail> {
  try {
    const body = (await response.json()) as { error?: unknown; code?: unknown };
    if (typeof body?.error === 'string' && body.error.length > 0) {
      return { message: body.error, code: isErrorCode(body.code) ? body.code : undefined };
    }
  } catch {
    /* not JSON, or truncated — fall through */
  }
  return { message: response.statusText || `HTTP ${response.status}` };
}

/**
 * Pull a human-readable reason out of a failed response.
 *
 * The contract says every non-2xx body is an `Error` object with a non-empty
 * `error`. A server that is wedged badly enough may not manage that, so this
 * degrades to the status text rather than throwing while building a throw.
 *
 * Exported so a caller that downloads a non-JSON response directly (a CSV,
 * say) with its own `fetch` can read the same error shape on a non-OK
 * response, instead of saving the error body as if it were the file.
 *
 * {@link describeFailureDetailed} is the version that also reads `code`; this
 * one stays message-only for the callers (`auth.ts`, `cases.ts`,
 * `DataExportPanel.tsx`) that only ever matched on `error`.
 */
export async function describeFailure(response: Response): Promise<string> {
  return (await describeFailureDetailed(response)).message;
}

/** `new ApiRequestError(...)` built from a failed `Response`, `code` included when the body carries one. */
async function apiErrorFor(response: Response, url: string): Promise<ApiRequestError> {
  const { message, code } = await describeFailureDetailed(response);
  return new ApiRequestError(response.status, message, url, code);
}

/**
 * `POST /panoramas`' query, every value percent-encoded — a space as `%20`,
 * never the form encoding's `+`, which a query parser that does not decode
 * `+` (ruxd's) would keep as a plus in the stored file name.
 */
export function panoramaUploadSearch(query: PanoramaUploadQuery): string {
  const parts: string[] = [];
  const add = (key: string, value: string | number | undefined) => {
    if (value !== undefined) parts.push(`${key}=${encodeURIComponent(String(value))}`);
  };
  add('x', query.x);
  add('y', query.y);
  add('z', query.z);
  add('yaw_deg', query.yaw_deg);
  add('filename', query.filename);
  add('timestamp', query.timestamp);
  return parts.length > 0 ? `?${parts.join('&')}` : '';
}

/**
 * One raw-JPEG POST through `XMLHttpRequest`, the one transport that reports
 * upload progress. Same-origin, so the session cookie rides along as with
 * `fetch`. Resolves with any HTTP answer; rejects on a dropped connection
 * (a `TypeError`, as `fetch`) or an abort (`AbortError`).
 */
function xhrUpload(
  url: string,
  body: Blob,
  onProgress: (fraction: number) => void,
  signal?: AbortSignal,
): Promise<{ status: number; statusText: string; text: string }> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('The upload was aborted', 'AbortError'));
      return;
    }
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url);
    xhr.setRequestHeader('Content-Type', 'image/jpeg');
    xhr.responseType = 'text';
    onProgress(0);
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && event.total > 0) onProgress(event.loaded / event.total);
    };
    xhr.onload = () => resolve({ status: xhr.status, statusText: xhr.statusText, text: xhr.responseText });
    xhr.onerror = () => reject(new TypeError('Failed to fetch'));
    xhr.onabort = () => reject(new DOMException('The upload was aborted', 'AbortError'));
    signal?.addEventListener('abort', () => xhr.abort(), { once: true });
    xhr.send(body);
  });
}

/**
 * One chunked upload into a case (`/imports/ifc/{id}`, ruxd PR #12;
 * `/imports/rtabmap/{id}`, ruxd PR #19 — the same protocol), bound to the
 * URL of the case it was started in. `O` is the body of `complete`.
 */
export class ChunkedUploadHandle<O extends object> {
  constructor(
    /** `…/cases/{cid}/imports/<kind>/{id}`. */
    readonly url: string,
    /** The upload as the `POST` answered it. */
    readonly upload: IfcUpload,
    private readonly doFetch: FetchLike,
  ) {}

  private async json<T>(url: string, init: Parameters<FetchLike>[1]): Promise<T> {
    const response = await this.doFetch(url, init);
    if (!response.ok) throw await apiErrorFor(response, url);
    return (await response.json()) as T;
  }

  /** `PUT ?offset=`: append one chunk (≤ `chunk_bytes`); 409 when `offset` is not the bytes received so far. */
  append(offset: number, chunk: Blob, signal?: AbortSignal): Promise<IfcUpload> {
    return this.json<IfcUpload>(`${this.url}?offset=${offset}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/octet-stream' },
      body: chunk,
      signal,
    });
  }

  /** `GET`: the upload's progress (404 once gone). */
  status(signal?: AbortSignal): Promise<IfcUpload> {
    return this.json<IfcUpload>(this.url, { method: 'GET', signal });
  }

  /**
   * `POST …/complete`: check the file (422 and deleted when it is not the
   * right kind) and submit the import job (202, like `submitJob`). 409 not
   * every byte has arrived (and, for a session, `session_already_imported`).
   */
  complete(options: O = {} as O, signal?: AbortSignal): Promise<Job> {
    return this.json<Job>(`${this.url}/complete`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(options),
      signal,
    });
  }

  /** `DELETE`: abandon the upload (204). */
  async abort(): Promise<void> {
    const response = await this.doFetch(this.url, { method: 'DELETE' });
    if (!response.ok) throw await apiErrorFor(response, this.url);
  }
}

/** One IFC upload (`/imports/ifc/{id}`, ruxd PR #12). */
export class IfcUploadHandle extends ChunkedUploadHandle<IfcImportOptions> {}

/** One RTAB-Map session upload (`/imports/rtabmap/{id}`, ruxd PR #19). */
export class RtabmapUploadHandle extends ChunkedUploadHandle<RtabmapCompleteOptions> {}

export class RuxApiClient {
  private baseUrl: string;
  private readonly serverBaseUrl: string;
  private readonly doFetch: FetchLike;

  constructor(options: ClientOptions = {}) {
    // Trailing slashes would produce `//clouds`, which the router does not match.
    this.baseUrl = (options.baseUrl ?? DEFAULT_BASE_URL).replace(/\/+$/, '');
    this.serverBaseUrl = (options.serverBaseUrl ?? DEFAULT_BASE_URL).replace(/\/+$/, '');
    this.doFetch = options.fetch ?? ((input, init) => authAwareFetch(input, init as RequestInit));
  }

  /**
   * Point this client at case `cid`. The app calls it once, before the first
   * render; switching case is a page load, so nothing in flight ever straddles
   * two cases.
   */
  selectCase(cid: string): void {
    this.baseUrl = caseBaseUrl(cid, this.serverBaseUrl);
    this.currentCase = cid;
  }

  /** The case selected with {@link selectCase}, if any. */
  get caseId(): string | undefined {
    return this.currentCase;
  }

  private currentCase: string | undefined;

  /** Absolute (or root-relative) URL for a contract path. Public for <img src>. */
  url(path: string, query?: Query): string {
    return `${this.baseUrl}${path}${buildQuery(query)}`;
  }

  /** URL of a server-level (not per-case) route. */
  private serverUrl(path: string, query?: Query): string {
    return `${this.serverBaseUrl}${path}${buildQuery(query)}`;
  }

  private async requestJson<T>(path: string, query?: Query, signal?: AbortSignal): Promise<T> {
    return this.getJsonAt<T>(this.url(path, query), signal);
  }

  private async getJsonAt<T>(url: string, signal?: AbortSignal): Promise<T> {
    const response = await this.doFetch(url, { method: 'GET', signal });
    if (!response.ok) {
      throw await apiErrorFor(response, url);
    }
    return (await response.json()) as T;
  }

  private async postJson<T>(path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
    const url = this.url(path);
    const response = await this.doFetch(url, {
      method: 'POST',
      // Required by the server on every mutating route — see rule 2 above.
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body ?? {}),
      signal,
    });
    if (!response.ok) {
      throw await apiErrorFor(response, url);
    }
    return (await response.json()) as T;
  }

  /** Full replacement of a resource, with a JSON body. */
  private async putJson<T>(path: string, body: unknown, signal?: AbortSignal): Promise<T> {
    const url = this.url(path);
    const response = await this.doFetch(url, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal,
    });
    if (!response.ok) {
      throw await apiErrorFor(response, url);
    }
    return (await response.json()) as T;
  }

  /**
   * A sparse edit against an existing resource.
   *
   * Same shape as {@link postJson}, and in particular the same
   * `Content-Type: application/json` — the server's CSRF gate keys on "is this
   * method mutating", not on POST, so a PATCH without the header is a 415 too.
   */
  private async patchJson<T>(path: string, body: unknown, signal?: AbortSignal): Promise<T> {
    const url = this.url(path);
    const response = await this.doFetch(url, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal,
    });
    if (!response.ok) {
      throw await apiErrorFor(response, url);
    }
    return (await response.json()) as T;
  }

  /** A DELETE that answers 204 with no body. */
  private async deleteNoContent(path: string): Promise<void> {
    const url = this.url(path);
    const response = await this.doFetch(url, { method: 'DELETE' });
    if (!response.ok) {
      throw await apiErrorFor(response, url);
    }
  }

  // ------------------------------------------------------------- meta ----

  health(signal?: AbortSignal): Promise<Health> {
    return this.requestJson<Health>('/health', undefined, signal);
  }

  async endpoints(signal?: AbortSignal): Promise<EndpointInfo[]> {
    const body = await this.getJsonAt<{ endpoints: EndpointInfo[] }>(this.serverUrl('/endpoints'), signal);
    return body.endpoints;
  }

  // ---------------------------------------------------- speckle token ----

  /**
   * `GET /me/speckle` (server-level): whether your Speckle token is set,
   * its server, `…abcd` and the account it was checked as. Never the token.
   * 403 for a case-scoped token or a share link.
   */
  speckleToken(signal?: AbortSignal): Promise<SpeckleTokenStatus> {
    return this.getJsonAt<SpeckleTokenStatus>(this.serverUrl('/me/speckle'), signal);
  }

  /**
   * `PUT /me/speckle`: ruxd checks the token against its Speckle server and
   * stores it. 400 malformed / refused by Speckle / server not allowed, 429 a
   * check is already running, 502 Speckle unreachable, 503 no validator, 403
   * not from a signed-in session. The answer never carries the token.
   */
  async setSpeckleToken(body: SpeckleTokenPut, signal?: AbortSignal): Promise<SpeckleTokenStatus> {
    const url = this.serverUrl('/me/speckle');
    const response = await this.doFetch(url, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal,
    });
    if (!response.ok) throw await apiErrorFor(response, url);
    return (await response.json()) as SpeckleTokenStatus;
  }

  /** `DELETE /me/speckle` (idempotent, 204). Local mode: 409 when the token comes from ruxd's configuration. */
  async deleteSpeckleToken(signal?: AbortSignal): Promise<void> {
    const url = this.serverUrl('/me/speckle');
    const response = await this.doFetch(url, { method: 'DELETE', signal });
    if (!response.ok) throw await apiErrorFor(response, url);
  }

  // ---------------------------------------------------------- project ----

  projectSummary(signal?: AbortSignal): Promise<ProjectSummary> {
    return this.requestJson<ProjectSummary>('/project', undefined, signal);
  }

  async projects(signal?: AbortSignal): Promise<ProjectInfo[]> {
    const body = await this.requestJson<{ projects: ProjectInfo[] }>(
      '/projects',
      undefined,
      signal,
    );
    return body.projects;
  }

  /**
   * Sparsely update (or create) a project metadata record.
   *
   * Only the fields present in `patch` are changed; absent fields keep their
   * current values. When the project id does not yet exist a new record is
   * created (upsert). Returns the full `ProjectInfo` after the edit.
   *
   * `null` explicitly clears a field (sets it to the empty/unset value).
   * `undefined` (or absent) means "leave untouched".
   */
  patchProject(
    id: string,
    patch: {
      name?: string;
      building_address?: string | null;
      year_of_construction?: number | null;
      survey_date?: string | null;
      survey_organisation?: string | null;
      notes?: string | null;
    },
    signal?: AbortSignal,
  ): Promise<ProjectInfo> {
    return this.patchJson<ProjectInfo>(`/projects/${encodeURIComponent(id)}`, patch, signal);
  }

  // ----------------------------------------------------------- clouds ----

  async clouds(signal?: AbortSignal): Promise<CloudInfo[]> {
    const body = await this.requestJson<{ clouds: CloudInfo[] }>('/clouds', undefined, signal);
    return body.clouds;
  }

  cloud(name: string, signal?: AbortSignal): Promise<CloudInfo> {
    return this.requestJson<CloudInfo>(`/clouds/${encodeURIComponent(name)}`, undefined, signal);
  }

  /**
   * One page of points, as JSON rows.
   *
   * `format` is sent explicitly even when the caller omits it. The contract's
   * default is `json` today, but this method decodes the JSON shape
   * unconditionally — so the request must name the format it is prepared to
   * parse rather than inherit whatever a future server decides to default to.
   * For the binary transport use {@link cloudPointsBinary}, which is a
   * different *return type*, not a different argument.
   */
  cloudPoints(
    name: string,
    options: CloudPointsQuery & { format?: 'json' | 'binary' } = {},
    signal?: AbortSignal,
  ): Promise<CloudPointsPage> {
    return this.requestJson<CloudPointsPage>(
      `/clouds/${encodeURIComponent(name)}/points`,
      { ...pointsQuery(options), format: options.format ?? 'json' },
      signal,
    );
  }

  /**
   * One page of points as a RUXP v1 body (`docs/gui/binary-points.md`, #283).
   *
   * Returns the raw `ArrayBuffer` rather than a parsed page: parsing belongs to
   * the viewport (`viewport/binaryPoints.ts`), and keeping the two apart is what
   * lets this client stay the one place that knows about HTTP.
   *
   * A server that predates the format answers **501**, which arrives as an
   * `ApiRequestError` with `isNotImplemented` — the signal a caller uses to
   * fall back to {@link cloudPoints} for the rest of its stream. Note that this
   * client does **not** read the `X-Ruxp-*` response headers: they are a `curl`
   * convenience, and the spec is explicit that the body header is the contract.
   */
  async cloudPointsBinary(
    name: string,
    options: CloudPointsQuery = {},
    signal?: AbortSignal,
  ): Promise<ArrayBuffer> {
    const url = this.url(`/clouds/${encodeURIComponent(name)}/points`, {
      ...pointsQuery(options),
      format: 'binary',
    });
    const response = await this.doFetch(url, { method: 'GET', signal });
    if (!response.ok) {
      // The failure body is still JSON on this route — the contract's `Error`
      // object — so the shared reason extraction applies unchanged.
      throw await apiErrorFor(response, url);
    }
    return response.arrayBuffer();
  }

  /**
   * The label legend of one `Label` cloud.
   *
   * The same map `GET /clouds/{name}` embeds; this route exists so an editor
   * can re-read just the legend after a write instead of the whole record.
   */
  /**
   * The spatial tile index of one cloud (#395).
   *
   * Present only for clouds stored in morton_10bit_bitrev order; a 404 arrives
   * as an `ApiRequestError` with `isNotFound`, the signal a viewport uses to
   * fall back to sequential paging.
   */
  cloudTiles(name: string, signal?: AbortSignal): Promise<CloudTileIndex> {
    return this.requestJson<CloudTileIndex>(
      `/clouds/${encodeURIComponent(name)}/tiles`,
      undefined,
      signal,
    );
  }

  async cloudLabels(name: string, signal?: AbortSignal): Promise<Record<string, string>> {
    const body = await this.requestJson<LabelLegend>(
      `/clouds/${encodeURIComponent(name)}/labels`,
      undefined,
      signal,
    );
    return body.labels;
  }

  /**
   * Rename label classes. **Sparse** — send only the ids that changed.
   *
   * Every id must already exist in the legend and every name must be non-empty
   * (both 400), and the `instances` cloud is refused with 409 because its names
   * are `SM<class>-<id> (<n>p)` records the pipeline parses back, not captions.
   * Returns the full legend after the rename, not just the edited ids.
   */
  async patchCloudLabels(
    name: string,
    labels: Record<string, string>,
    signal?: AbortSignal,
  ): Promise<Record<string, string>> {
    const body = await this.patchJson<LabelLegend>(
      `/clouds/${encodeURIComponent(name)}/labels`,
      { labels },
      signal,
    );
    return body.labels;
  }

  // ----------------------------------------------------------- meshes ----

  async meshes(signal?: AbortSignal): Promise<MeshInfo[]> {
    const body = await this.requestJson<{ meshes: MeshInfo[] }>('/meshes', undefined, signal);
    return body.meshes;
  }

  mesh(name: string, signal?: AbortSignal): Promise<MeshInfo> {
    return this.requestJson<MeshInfo>(`/meshes/${encodeURIComponent(name)}`, undefined, signal);
  }

  /** URL of the raw geometry blob, in the mesh's stored format. */
  meshDataUrl(name: string): string {
    return this.url(`/meshes/${encodeURIComponent(name)}/data`);
  }

  async meshTextures(name: string, signal?: AbortSignal): Promise<TextureInfo[]> {
    const body = await this.requestJson<{ textures: TextureInfo[] }>(
      `/meshes/${encodeURIComponent(name)}/textures`,
      undefined,
      signal,
    );
    return body.textures;
  }

  meshTextureUrl(name: string, texture: string): string {
    return this.url(
      `/meshes/${encodeURIComponent(name)}/textures/${encodeURIComponent(texture)}`,
    );
  }

  // ---------------------------------------------------------- gsplats ----

  /**
   * Gaussian splats stored in this project.
   *
   * An empty array is the normal answer for a project nobody has run
   * `rux create gsplat` on — not a `404`, and not an error. Unpaged by
   * contract: a project holds a handful of splats, not a number that grows
   * with the size of the scan.
   */
  async gsplats(signal?: AbortSignal): Promise<GsplatInfo[]> {
    const body = await this.requestJson<{ gsplats: GsplatInfo[] }>(
      '/gsplats',
      undefined,
      signal,
    );
    return body.gsplats;
  }

  gsplat(name: string, signal?: AbortSignal): Promise<GsplatInfo> {
    return this.requestJson<GsplatInfo>(
      `/gsplats/${encodeURIComponent(name)}`,
      undefined,
      signal,
    );
  }

  /**
   * URL of one splat's PLY blob, for the renderer to fetch itself.
   *
   * A URL rather than an `ArrayBuffer`: the splat loader streams and parses the
   * file on a worker, and routing hundreds of megabytes through this client
   * first would buffer the whole thing twice for no gain. The same reasoning as
   * {@link meshDataUrl}.
   */
  gsplatDataUrl(name: string): string {
    return this.url(`/gsplats/${encodeURIComponent(name)}/data`);
  }

  // --------------------------------------------------------- pose graph ----

  /**
   * Pose-graph nodes (frame poses) and edges (post-solve residuals).
   *
   * Nodes are all sensor frames with a stored world pose.  Edges are populated
   * by `rux optimize`; an un-optimised project returns an empty `edges` array.
   * Unpaged — sized for diagnostic use, not streaming.
   */
  posegraph(signal?: AbortSignal): Promise<PoseGraph> {
    return this.requestJson<PoseGraph>('/posegraph', undefined, signal);
  }

  /**
   * Add a manual pose-graph edge (#407).
   *
   * The edge is stored with `residual = 0.0`. After adding edges, re-run
   * `rux optimize` to propagate the new constraints.
   *
   * Returns 409 when the project has never been optimised (no graph to edit).
   */
  addPoseGraphEdge(edge: PoseGraphEdgeCreate, signal?: AbortSignal): Promise<PoseGraphEdge> {
    return this.postJson<PoseGraphEdge>('/posegraph/edges', edge, signal);
  }

  /**
   * Delete a pose-graph edge by from/to node id (#407).
   *
   * When `type` is supplied only edges of that type are removed.
   * Returns 404 when no matching edge exists.
   */
  async deletePoseGraphEdge(
    from: number,
    to: number,
    type?: PoseGraphEdgeType,
    signal?: AbortSignal,
  ): Promise<PoseGraphEdgeDeleteResult> {
    const url = this.url(`/posegraph/edges/${from}/${to}`, { type });
    const response = await this.doFetch(url, { method: 'DELETE', signal });
    if (!response.ok) {
      throw await apiErrorFor(response, url);
    }
    return (await response.json()) as PoseGraphEdgeDeleteResult;
  }

  /**
   * ICP-refine the relative pose between two depth frames (#465).
   *
   * Runs point-to-point ICP between the depth clouds of `from` and `to`.
   * Returns the refined relative pose, RMS fitness (metres), inlier fraction,
   * and whether ICP converged.
   *
   * Returns 422 when a frame has no depth or pose; 503 when ICP is not
   * available in this build.
   */
  refinePoseGraphIcp(
    req: IcpRefineRequest,
    signal?: AbortSignal,
  ): Promise<IcpRefineResult> {
    return this.postJson<IcpRefineResult>('/posegraph/icp', req, signal);
  }

  // ---------------------------------- pose-graph editor (ruxd PR #19) ----
  // Every write needs editor/owner, is refused with 409 while a job runs,
  // and is followed by a `posegraph.changed` event.

  /** `GET /posegraph/blocks`: the trajectory's blocks, in node order. */
  poseBlocks(signal?: AbortSignal): Promise<PoseBlockList> {
    return this.requestJson<PoseBlockList>('/posegraph/blocks', undefined, signal);
  }

  /** `PUT /posegraph/blocks`: replace the list (422 frames left out / overlap / across sessions). */
  replacePoseBlocks(blocks: PoseBlockInput[], signal?: AbortSignal): Promise<PoseBlockList> {
    return this.putJson<PoseBlockList>('/posegraph/blocks', { blocks }, signal);
  }

  /** `GET /posegraph/blocks/suggest`: suggested splits with reasons, glitches, storey bands. Not stored. */
  suggestPoseBlocks(query: BlockSuggestQuery = {}, signal?: AbortSignal): Promise<BlockSuggestions> {
    return this.requestJson<BlockSuggestions>('/posegraph/blocks/suggest', { ...query }, signal);
  }

  /** `POST /posegraph/blocks/split`: start a new block at `nodeId` (422 not posed / already a start). */
  splitPoseBlock(nodeId: number, name?: string, signal?: AbortSignal): Promise<PoseBlockList> {
    return this.postJson<PoseBlockList>('/posegraph/blocks/split', name ? { node_id: nodeId, name } : { node_id: nodeId }, signal);
  }

  /** `POST /posegraph/blocks/merge`: merge `second` into `first` (neighbours in one session). */
  mergePoseBlocks(first: number, second: number, signal?: AbortSignal): Promise<PoseBlockList> {
    return this.postJson<PoseBlockList>('/posegraph/blocks/merge', { first, second }, signal);
  }

  /** `PATCH /posegraph/blocks/{bid}`: rename. */
  renamePoseBlock(id: number, name: string, signal?: AbortSignal): Promise<PoseBlockList> {
    return this.patchJson<PoseBlockList>(`/posegraph/blocks/${id}`, { name }, signal);
  }

  /** `POST /posegraph/blocks/{bid}/detach`: cut the joint before the block, or `attach` to undo it. Records a version. */
  detachPoseBlock(id: number, attach = false, signal?: AbortSignal): Promise<DetachResult> {
    return this.postJson<DetachResult>(`/posegraph/blocks/${id}/detach`, attach ? { attach: true } : {}, signal);
  }

  /**
   * `POST /posegraph/blocks/transform`: move / rotate the selection as a
   * re-seed (or `mode: local`). `preview: true` writes nothing and returns
   * the moved poses; an apply records one pose version.
   */
  transformPoseBlocks(request: BlockTransformRequest, signal?: AbortSignal): Promise<BlockTransformResult> {
    return this.postJson<BlockTransformResult>('/posegraph/blocks/transform', request, signal);
  }

  /** `POST /posegraph/odometry/rebuild`: odometry from the initial poses (`from_current` re-seeds first). */
  rebuildPoseOdometry(fromCurrent = false, signal?: AbortSignal): Promise<RebuildOdometryResult> {
    return this.postJson<RebuildOdometryResult>('/posegraph/odometry/rebuild', fromCurrent ? { from_current: true } : {}, signal);
  }

  /** `GET /posegraph/versions`: every pose version, oldest first, and the current one. */
  poseVersions(signal?: AbortSignal): Promise<PoseVersionList> {
    return this.requestJson<PoseVersionList>('/posegraph/versions', undefined, signal);
  }

  /** `GET /posegraph/versions/{a}/diff/{b}`: the frames moved, largest shift first. */
  diffPoseVersions(a: number, b: number, minShiftM?: number, signal?: AbortSignal): Promise<PoseVersionDiff> {
    return this.requestJson<PoseVersionDiff>(`/posegraph/versions/${a}/diff/${b}`, { min_shift_m: minShiftM }, signal);
  }

  /** `POST /posegraph/versions/{vid}/adopt`: restore a version and make it current. */
  adoptPoseVersion(id: number, signal?: AbortSignal): Promise<PoseVersionChange> {
    return this.postJson<PoseVersionChange>(`/posegraph/versions/${id}/adopt`, {}, signal);
  }

  /** `POST /posegraph/undo`: restore the current version's parent (409 `nothing_to_undo`). */
  undoPoseVersion(signal?: AbortSignal): Promise<PoseVersionChange> {
    return this.postJson<PoseVersionChange>('/posegraph/undo', {}, signal);
  }

  /** `POST /posegraph/redo`: restore the newest later version (409 `nothing_to_redo`). */
  redoPoseVersion(signal?: AbortSignal): Promise<PoseVersionChange> {
    return this.postJson<PoseVersionChange>('/posegraph/redo', {}, signal);
  }

  /** `GET /posegraph/edits`: the working pose-edits document. */
  poseEdits(signal?: AbortSignal): Promise<PoseEditsDocument> {
    return this.requestJson<PoseEditsDocument>('/posegraph/edits', undefined, signal);
  }

  /** `PUT /posegraph/edits`: replace it (422 `posegraph_edit_refused`). */
  putPoseEdits(doc: PoseEditsDocument, signal?: AbortSignal): Promise<PoseEditsDocument> {
    return this.putJson<PoseEditsDocument>('/posegraph/edits', doc, signal);
  }

  // ----------------------------------------------------------- frames ----

  /**
   * Frame ids, optionally narrowed to those that do or do not carry a mask.
   *
   * `segmented` is a **server-side** filter and must be used as one: the
   * client-side alternative is `GET /frames/{id}` per frame just to read one
   * boolean, and that route decodes the depth and confidence blobs to answer.
   *
   * `total_count` / `segmented_count` always describe the whole scan, never the
   * filtered result — use `ids.length` for the size of the filtered set.
   */
  frames(
    options: { segmented?: boolean } = {},
    signal?: AbortSignal,
  ): Promise<FrameList> {
    return this.requestJson<FrameList>('/frames', { segmented: options.segmented }, signal);
  }

  frame(id: number, signal?: AbortSignal): Promise<FrameInfo> {
    return this.requestJson<FrameInfo>(`/frames/${id}`, undefined, signal);
  }

  /**
   * URL of one of a frame's images, in its **stored** orientation.
   *
   * `max_size` downscales the longest edge — what makes a grid of hundreds of
   * thumbnails affordable. `normalize` asks for a *displayable* rendering
   * rather than the stored measurement, and is required for `depth`,
   * `confidence` and `segmentation`: those are 16-bit single-channel PNGs, and
   * a browser decoding a 3 m room stored in millimetres (3000 of 65535) paints
   * near-black. The normalised image carries **no metric scale**.
   *
   * The server reports the mapped range in `X-Image-Range-Min`/`-Max`, which an
   * `<img>` cannot read. That is deliberate on both sides: they are a `curl`
   * convenience, so nothing here is built on them.
   */
  frameImageUrl(
    id: number,
    kind: FrameImageKind = 'color',
    options: { maxSize?: number; normalize?: boolean } = {},
  ): string {
    return this.url(`/frames/${id}/image`, {
      kind,
      max_size: options.maxSize,
      // Sent only when true: the contract's default is already false, and an
      // explicit `normalize=false` on every colour thumbnail is noise in a log.
      normalize: options.normalize ? true : undefined,
    });
  }

  /**
   * Match feature descriptors between two frames.
   *
   * **Backend endpoint not yet implemented** as of #446.  Calling this method
   * will receive a 404 from the current server.  The full contract — request
   * body, success/error shapes, and matching pipeline — is documented on
   * `DescriptorMatchResult` in `api/types.ts`.
   *
   * The UI catches the resulting `ApiRequestError` with `isNotFound` /
   * `isNotImplemented` and renders a "backend not yet available" message,
   * so the stub is safe to ship.
   */
  frameDescriptorMatch(
    frameA: number,
    frameB: number,
    method: DescriptorMethod,
    signal?: AbortSignal,
  ): Promise<DescriptorMatchResult> {
    return this.postJson<DescriptorMatchResult>(
      `/frames/${frameA}/descriptor-match/${frameB}`,
      { method },
      signal,
    );
  }

  /**
   * Run SAM3 segmentation on one frame and optionally store the label mask (#409).
   *
   * Always uses the server's managed SAM3 model. Statuses worth branching on:
   * 503 is ambiguous — the model is being prepared (poll
   * {@link sam3Status}) *or* the project database was busy; 500 "SAM3 model
   * preparation failed…" means the managed download/build failed; 409 means a
   * pipeline job holds the write lock. `data/sam3Provisioning.ts` owns that
   * decision so every caller handles it the same way.
   */
  segmentFrame(
    id: number,
    request: FrameSegmentRequest,
    signal?: AbortSignal,
  ): Promise<FrameSegmentResult> {
    return this.postJson<FrameSegmentResult>(`/frames/${id}/segment`, request, signal);
  }

  /**
   * Project one prompt's pixels of a frame's **saved** segmentation into the
   * base cloud and file them as a new instance + survey part (resource).
   * Answers 201; 422 when the frame has no pose/depth/saved mask or the mask
   * covers no visible point. The server then broadcasts `clouds.changed`.
   */
  segmentResource(
    id: number,
    request: SegmentResourceRequest,
    signal?: AbortSignal,
  ): Promise<SegmentResourceResult> {
    return this.postJson<SegmentResourceResult>(`/frames/${id}/segment/resource`, request, signal);
  }

  /**
   * Provisioning status of the managed SAM3 model — a pure probe that never
   * starts preparation. `cuda` picks the slot; omit it to report the slot a
   * segment request without `use_cuda` would use. 501 when the server has
   * no managed model provider.
   */
  sam3Status(cuda?: boolean, signal?: AbortSignal): Promise<Sam3ModelStatus> {
    // Server-level: one managed model serves every case.
    return this.getJsonAt<Sam3ModelStatus>(this.serverUrl('/models/sam3/status', { cuda }), signal);
  }

  /**
   * Sensor frames that see a world-space point, ranked by centrality (#453).
   *
   * The first frame in the result is the best "source image" for the point —
   * the one where it projects closest to the principal point.
   */
  pointVisibility(
    x: number,
    y: number,
    z: number,
    options: { maxDepth?: number; limit?: number } = {},
    signal?: AbortSignal,
  ): Promise<FrameVisibilityList> {
    return this.requestJson<FrameVisibilityList>(
      '/frames/visibility',
      { x, y, z, max_depth: options.maxDepth, limit: options.limit },
      signal,
    );
  }

  /**
   * Sensor frames that see an instance's centroid, ranked by centrality (#453).
   *
   * Convenience wrapper over `/frames/visibility`: the server computes the
   * centroid for the caller rather than requiring a two-step fetch.
   */
  instanceVisibility(
    cloud: string,
    instanceId: number,
    options: { maxDepth?: number; limit?: number } = {},
    signal?: AbortSignal,
  ): Promise<FrameVisibilityList> {
    return this.requestJson<FrameVisibilityList>(
      `/instances/${encodeURIComponent(cloud)}/${instanceId}/frames`,
      { max_depth: options.maxDepth, limit: options.limit },
      signal,
    );
  }

  // -------------------------------------------------------- panoramas ----

  async panoramas(signal?: AbortSignal): Promise<PanoramaInfo[]> {
    const body = await this.requestJson<{ panoramas: PanoramaInfo[] }>(
      '/panoramas',
      undefined,
      signal,
    );
    return body.panoramas;
  }

  panorama(id: number, signal?: AbortSignal): Promise<PanoramaInfo> {
    return this.requestJson<PanoramaInfo>(`/panoramas/${id}`, undefined, signal);
  }

  /**
   * Place a panorama by hand (`PATCH /panoramas/{id}`, ruxd#11): a whole
   * pose, or a nudge of any of `x`/`y`/`z`/`yaw_deg` (the rest is kept).
   * Answers the panorama as `GET` does, now `pose_source: manual`. Editor or
   * owner; 409 while a job holds the case. The server then sends
   * `panoramas.changed`.
   */
  placePanorama(id: number, patch: PanoramaPlacementPatch, signal?: AbortSignal): Promise<PanoramaInfo> {
    return this.patchJson<PanoramaInfo>(`/panoramas/${id}`, patch, signal);
  }

  /**
   * Add a 360 panorama at a manual position (`POST /panoramas`, ruxd#11):
   * the body is the raw equirect JPEG (≤ 64 MiB, width == 2 × height), the
   * placement the query. Answers 201 with the new panorama. `onProgress`
   * reports the upload's sent fraction (0..1); with it the request goes
   * through `XMLHttpRequest`, the one transport that reports upload
   * progress (same-origin, so the session cookie rides along as with
   * `fetch`).
   */
  async uploadPanorama(
    image: Blob,
    query: PanoramaUploadQuery,
    options: { onProgress?: (fraction: number) => void; signal?: AbortSignal } = {},
  ): Promise<PanoramaInfo> {
    const url = `${this.url('/panoramas')}${panoramaUploadSearch(query)}`;
    const { onProgress, signal } = options;
    if (!onProgress || typeof XMLHttpRequest === 'undefined') {
      const response = await this.doFetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'image/jpeg' },
        body: image,
        signal,
      });
      if (!response.ok) throw await apiErrorFor(response, url);
      return (await response.json()) as PanoramaInfo;
    }
    // A 401 re-bridges the session and sends once more, as `fetch` does.
    const answer = await authRetry(url, () => xhrUpload(url, image, onProgress, signal));
    let body: unknown = null;
    try {
      body = answer.text ? JSON.parse(answer.text) : null;
    } catch {
      body = null;
    }
    if (answer.status >= 200 && answer.status < 300) {
      onProgress(1);
      return body as PanoramaInfo;
    }
    const parsed = body as { error?: unknown; code?: unknown } | null;
    const error = parsed?.error;
    const code = isErrorCode(parsed?.code) ? parsed.code : undefined;
    throw new ApiRequestError(
      answer.status,
      typeof error === 'string' && error ? error : answer.statusText || `HTTP ${answer.status}`,
      url,
      code,
    );
  }

  /**
   * URL of the equirectangular JPEG, in its **stored** orientation.
   *
   * `maxSize` downscales the longest edge, and a picker must use it: a stored
   * equirect is routinely 8192x4096 and several megabytes, so a strip of
   * twenty 96-pixel thumbnails is otherwise tens of megabytes.
   */
  panoramaImageUrl(id: number, options: { maxSize?: number } = {}): string {
    return this.url(`/panoramas/${id}/image`, { max_size: options.maxSize });
  }

  /**
   * Run SAM3 segmentation on a 360 panorama and optionally store the equirect
   * label mask (#448).
   *
   * Uses the managed SAM3 model; the 503/500/409 statuses mean what they do
   * for {@link segmentFrame}.
   */
  segmentPanorama(
    id: number,
    request: PanoramaSegmentRequest,
    signal?: AbortSignal,
  ): Promise<PanoramaSegmentResult> {
    return this.postJson<PanoramaSegmentResult>(
      `/panoramas/${id}/segment`,
      request,
      signal,
    );
  }

  /**
   * Project one class of a panorama's **saved** equirect segmentation into
   * the base cloud and file it as a new instance + survey part — the 360°
   * counterpart of {@link segmentResource}.
   *
   * **Backend endpoint not yet implemented** (CONTRACT-GAPS 15): ruxd has
   * `POST /panoramas/{id}/segment` but no `…/segment/resource`, so a real
   * server answers 404 and the UI says so. The intended shape mirrors the
   * frame route exactly: same request (`mask_revision` unused until the
   * panorama run reports one) and the same 201 `SegmentResourceResult`. The
   * mock (`scripts/mock-ruxd/panoramas.ts`) implements it.
   */
  segmentPanoramaResource(
    id: number,
    request: SegmentResourceRequest,
    signal?: AbortSignal,
  ): Promise<SegmentResourceResult> {
    return this.postJson<SegmentResourceResult>(`/panoramas/${id}/segment/resource`, request, signal);
  }

  // ------------------------------------------------------- components ----

  async components(type?: string, signal?: AbortSignal): Promise<ComponentInfo[]> {
    const body = await this.requestJson<{ components: ComponentInfo[] }>(
      '/components',
      { type },
      signal,
    );
    return body.components;
  }

  component(name: string, signal?: AbortSignal): Promise<ComponentDetail> {
    return this.requestJson<ComponentDetail>(
      `/components/${encodeURIComponent(name)}`,
      undefined,
      signal,
    );
  }

  // -------------------------------------------------------- materials ----

  async materials(signal?: AbortSignal): Promise<MaterialInfo[]> {
    const body = await this.requestJson<{ materials: MaterialInfo[] }>(
      '/materials',
      undefined,
      signal,
    );
    return body.materials;
  }

  material(guid: string, signal?: AbortSignal): Promise<MaterialDetail> {
    return this.requestJson<MaterialDetail>(
      `/materials/${encodeURIComponent(guid)}`,
      undefined,
      signal,
    );
  }

  /**
   * Add, change or clear passport properties. **Sparse** — only what changed.
   *
   * A `null` value deletes the property; a string sets it. Anything not named
   * is left alone, including the MaterialEPAS fields this GUI does not model.
   * Returns the passport as `GET` would render it after the edit.
   */
  patchMaterial(
    guid: string,
    properties: Record<string, string | null>,
    signal?: AbortSignal,
  ): Promise<MaterialDetail> {
    return this.patchJson<MaterialDetail>(
      `/materials/${encodeURIComponent(guid)}`,
      { properties },
      signal,
    );
  }

  /** Create a new (blank) material passport. Returns the created passport. */
  async createMaterial(body?: MaterialCreate): Promise<MaterialDetail> {
    return this.postJson<MaterialDetail>('/materials', body ?? {});
  }

  /**
   * Upload (or replace) a material's thumbnail image.
   *
   * Deliberately bypasses the JSON transport: the body is the raw file bytes and
   * the `Content-Type` is the file's own MIME type, so `this.postJson` (which is
   * JSON-only) cannot be used here.
   */
  async uploadThumbnail(guid: string, file: File): Promise<void> {
    const url = this.url(`/materials/${encodeURIComponent(guid)}/thumbnail`);
    const response = await authAwareFetch(url, {
      method: 'PUT',
      headers: { 'Content-Type': file.type || 'image/jpeg' },
      body: file,
    });
    if (!response.ok) {
      throw await apiErrorFor(response, url);
    }
  }

  // ------------------------------------------------- resource columns ----

  /** All user-defined resource column definitions (`col:<id>` keys). */
  resourceColumns(signal?: AbortSignal): Promise<PropertyDefinition[]> {
    return this.requestJson<PropertyDefinition[]>('/resources/columns', undefined, signal);
  }

  /** Create a user column. Returns the created definition; its key id is `col:<id>`. */
  createResourceColumn(body: ResourceColumnCreate): Promise<PropertyDefinition> {
    return this.postJson<PropertyDefinition>('/resources/columns', body);
  }

  /** Sparsely update a user column. A rename also renames its stored values (spec §4.3). */
  updateResourceColumn(id: string, patch: Partial<ResourceColumnCreate>): Promise<PropertyDefinition> {
    return this.patchJson<PropertyDefinition>(`/resources/columns/${encodeURIComponent(id)}`, patch);
  }

  /** Delete a user column. The server answers 204 (no body). */
  async deleteResourceColumn(id: string): Promise<void> {
    await this.deleteNoContent(`/resources/columns/${encodeURIComponent(id)}`);
  }

  // -------------------------------------------------------- resources ----

  /** The key catalogue: leksikon, user columns and built-ins (spec §4.3). */
  resourceKeys(signal?: AbortSignal): Promise<ResourceKey[]> {
    return this.requestJson<ResourceKey[]>('/resources/keys', undefined, signal);
  }

  /**
   * Every resource. With `templateId`, `values` holds exactly that template's
   * resolved keys (missing = null); without, every key the resource has.
   */
  async resources(templateId?: number, signal?: AbortSignal): Promise<Resource[]> {
    const body = await this.requestJson<{ resources: Resource[] }>(
      '/resources',
      templateId === undefined ? undefined : { template: templateId },
      signal,
    );
    return body.resources;
  }

  /**
   * Set or clear values (`null` clears; clearing an absent value succeeds).
   * Writes route by key scope; a type-scoped write changes every part of the
   * type, and those parts come back in `siblings`. 400 names a bad key/value.
   */
  patchResource(code: string, values: Record<string, string | null>): Promise<ResourcePatchResult> {
    return this.patchJson<ResourcePatchResult>(`/resources/${encodeURIComponent(code)}`, { values });
  }

  /** Create a manual part with the next code for the type. */
  createResource(body: ResourceCreate): Promise<Resource> {
    return this.postJson<Resource>('/resources', body);
  }

  /** Delete any part; a scan-backed one is tombstoned so a sync does not re-create it. 204, no body. */
  async deleteResource(code: string): Promise<void> {
    await this.deleteNoContent(`/resources/${encodeURIComponent(code)}`);
  }

  // -------------------------------------------------------- templates ----

  /** Every template, each resolved (`resolved_keys`, `missing`). */
  async templates(signal?: AbortSignal): Promise<Template[]> {
    const body = await this.requestJson<{ templates: Template[] }>('/templates', undefined, signal);
    return body.templates;
  }

  /** Create a template. A duplicate name is a 409. */
  createTemplate(body: TemplateCreate, signal?: AbortSignal): Promise<Template> {
    return this.postJson<Template>('/templates', body, signal);
  }

  /** Copy a template as "<name> (kopi)" (numeric suffix until unique). */
  duplicateTemplate(id: number): Promise<Template> {
    return this.postJson<Template>(`/templates/${id}/duplicate`);
  }

  /** Sparse edit of name / members / csv. A duplicate name is a 409. */
  patchTemplate(id: number, patch: TemplatePatch): Promise<Template> {
    return this.patchJson<Template>(`/templates/${id}`, patch);
  }

  /** Delete a template. The server answers 204 (no body). */
  async deleteTemplate(id: number): Promise<void> {
    await this.deleteNoContent(`/templates/${id}`);
  }

  /** Re-insert any missing seed template (materialepas, screening). Re-list afterwards. */
  async restoreSeedTemplates(signal?: AbortSignal): Promise<void> {
    await this.postJson<unknown>('/templates/restore-seeds', {}, signal);
  }

  /** The resources CSV for one template. Fetched directly (not through this client) so a non-OK response can be read as an error rather than saved as the file. */
  resourcesExportCsvUrl(templateId: number): string {
    return this.url('/resources/export.csv', { template: templateId });
  }

  // -------------------------------------------------------- instances ----

  async instances(cloud: string, signal?: AbortSignal): Promise<InstanceInfo[]> {
    const body = await this.requestJson<{ cloud: string; instances: InstanceInfo[] }>(
      `/instances/${encodeURIComponent(cloud)}`,
      undefined,
      signal,
    );
    return body.instances;
  }

  /**
   * Link a material passport to an instance (upsert).
   *
   * The material must already exist — mint one with {@link createMaterial} first.
   * Returns the updated instance row.
   */
  async linkInstanceMaterial(
    cloud: string,
    instanceId: number,
    materialGuid: string,
  ): Promise<InstanceInfo> {
    return this.putJson<InstanceInfo>(
      `/instances/${encodeURIComponent(cloud)}/${instanceId}/material`,
      { guid: materialGuid },
    );
  }

  /**
   * Fetch the ranked source frames for an instance centroid.
   *
   * Returns the `frames` array from the `/instances/{cloud}/{id}/frames`
   * response, which is the same shape as `/frames/visibility`.  The top
   * element (index 0) is the most-central frame — the "best" source image.
   */
  async instanceFrames(
    cloud: string,
    instanceId: number,
    signal?: AbortSignal,
  ): Promise<VisibleFrame[]> {
    const body = await this.requestJson<{ frames: VisibleFrame[] }>(
      `/instances/${encodeURIComponent(cloud)}/${instanceId}/frames`,
      undefined,
      signal,
    );
    return body.frames ?? [];
  }

  /**
   * Placeable 360 panoramas near an instance, nearest first, each with the
   * equirect `u,v` (0..1) its centroid lands on.
   */
  instancePanoramas(
    cloud: string,
    instanceId: number,
    signal?: AbortSignal,
  ): Promise<InstancePanoramaList> {
    return this.requestJson<InstancePanoramaList>(
      `/instances/${encodeURIComponent(cloud)}/${instanceId}/panoramas`,
      undefined,
      signal,
    );
  }

  // --------------------------------------------------------- pipeline ----

  async stages(signal?: AbortSignal): Promise<StageInfo[]> {
    const body = await this.requestJson<{ stages: StageInfo[] }>('/stages', undefined, signal);
    return body.stages;
  }

  /**
   * Re-check one stage's input contract.
   *
   * Returns the same record `/stages` does for that stage, so a card can be
   * substituted in place after a run finishes rather than refetching the whole
   * catalogue to learn that `clouds` succeeding unblocked `planes`.
   */
  stageValidation(stage: string, signal?: AbortSignal): Promise<StageInfo> {
    return this.requestJson<StageInfo>(
      `/stages/${encodeURIComponent(stage)}/validation`,
      undefined,
      signal,
    );
  }

  async pipelineLog(limit?: number, signal?: AbortSignal): Promise<PipelineLogEntry[]> {
    const body = await this.requestJson<{ entries: PipelineLogEntry[] }>(
      '/pipeline-log',
      { limit },
      signal,
    );
    return body.entries;
  }

  // ------------------------------------------------------------- jobs ----

  async jobs(signal?: AbortSignal): Promise<Job[]> {
    const body = await this.requestJson<{ jobs: Job[] }>('/jobs', undefined, signal);
    return body.jobs;
  }

  job(id: string, signal?: AbortSignal): Promise<Job> {
    return this.requestJson<Job>(`/jobs/${encodeURIComponent(id)}`, undefined, signal);
  }

  /**
   * Submit a stage run.
   *
   * The returned `status` is a snapshot at response time and is **not** to be
   * asserted on — an idle server may already have started, or even failed, the
   * job. Drive the UI from `/events` or from a poll.
   */
  submitJob(request: JobRequest, signal?: AbortSignal): Promise<Job> {
    return this.postJson<Job>('/jobs', request, signal);
  }

  /**
   * Submit a chain of stages (`POST /job-chains`, 202): every job of it, in
   * order, as at submission. Like `submitJob`, progress arrives as events.
   */
  async submitJobChain(request: JobChainRequest, signal?: AbortSignal): Promise<Job[]> {
    const body = await this.postJson<{ jobs: Job[] }>('/job-chains', request, signal);
    return body.jobs;
  }

  /** Request cancellation. Idempotent; the outcome arrives as `job.finished`. */
  cancelJob(id: string, signal?: AbortSignal): Promise<Job> {
    return this.postJson<Job>(`/jobs/${encodeURIComponent(id)}/cancel`, {}, signal);
  }

  // ---------------------------------------------------------- imports ----

  /**
   * `POST /imports/speckle` (ruxd PR #12): import a Revit model from Speckle
   * as an `import_speckle` job, with the caller's own Speckle token (never
   * sent here). Answers like {@link submitJob}. 400 no token saved / server
   * not allowed or not the token's / a bad id or option; 403 a viewer, or a
   * principal without a Speckle token of its own (case token, share link).
   */
  importSpeckle(request: SpeckleImportRequest, signal?: AbortSignal): Promise<Job> {
    return this.postJson<Job>('/imports/speckle', request, signal);
  }

  /**
   * `POST /imports/ifc`: start a chunked IFC upload (201; 413 over
   * `max_bytes`, 429 four in progress). The answer carries a handle pinned
   * to the case selected *now*: every later `PUT`, `GET`, `complete` and
   * `DELETE` of this upload goes to that case's URL, whatever the client is
   * pointed at by then.
   */
  async beginIfcUpload(name: string, size: number, signal?: AbortSignal): Promise<IfcUploadHandle> {
    const base = this.url('/imports/ifc');
    const response = await this.doFetch(base, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, size }),
      signal,
    });
    if (!response.ok) throw await apiErrorFor(response, base);
    const upload = (await response.json()) as IfcUpload;
    return new IfcUploadHandle(`${base}/${encodeURIComponent(upload.id)}`, upload, this.doFetch);
  }

  /**
   * `POST /imports/rtabmap` (ruxd PR #19): start a chunked upload of one
   * RTAB-Map session database (201; 413 `rtabmap_too_large` /
   * `rtabmap_quota_exceeded`, 429 `rtabmap_upload_cap`, 507 no space). The
   * handle is pinned to the case selected now, like {@link beginIfcUpload}.
   */
  async beginRtabmapUpload(name: string, size: number, signal?: AbortSignal): Promise<RtabmapUploadHandle> {
    const base = this.url('/imports/rtabmap');
    const response = await this.doFetch(base, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, size }),
      signal,
    });
    if (!response.ok) throw await apiErrorFor(response, base);
    const upload = (await response.json()) as IfcUpload;
    return new RtabmapUploadHandle(`${base}/${encodeURIComponent(upload.id)}`, upload, this.doFetch);
  }

  // --------------------------------------------------------- reports ----

  /**
   * Generate a Ressourcekortlægning PDF server-side and store it.
   *
   * Invokes `typst compile` on the server, stores the PDF in the project
   * database, and returns the new version metadata. `templateId` adds a
   * Ressourcetabel section built from that template's columns; null or
   * omitted leaves it out. Writer-locked: a 409 means a pipeline stage is
   * holding the lock; a 503 means a transient busy — the `ApiRequestError`
   * properties distinguish the two.
   */
  generateReport(templateId?: number | null, signal?: AbortSignal): Promise<ReportPdfVersion> {
    const body = templateId == null ? {} : { resource_template_id: templateId };
    return this.postJson<ReportPdfVersion>('/reports/ressourcekortlaegning', body, signal);
  }

  /**
   * List stored Ressourcekortlægning PDF versions, newest first.
   *
   * Returns an empty array for a project that has never had a report generated.
   */
  async listReportVersions(signal?: AbortSignal): Promise<ReportPdfVersion[]> {
    const body = await this.requestJson<{ versions: ReportPdfVersion[] }>(
      '/reports/ressourcekortlaegning',
      undefined,
      signal,
    );
    return body.versions;
  }

  /**
   * URL of a stored PDF, for use as an `<a href>` or in `window.open`.
   *
   * Returns a URL string so the browser downloads the raw PDF bytes directly
   * without routing them through JS.
   */
  reportPdfUrl(id: number): string {
    return this.url(`/reports/ressourcekortlaegning/${id}`);
  }

  /**
   * Ask for the report as PNG pages (contract gap 4). 200 = compiled (or
   * failed), 202 = compiling with `sections` already filled in — poll
   * {@link reportPreviewStatus} with its digest. A 404 here means a ruxd
   * from before the preview (or an unknown template).
   */
  async reportPreview(
    options: { ppi?: number; templateId?: number | null } = {},
    signal?: AbortSignal,
  ): Promise<{ status: 200 | 202; preview: ReportPreview }> {
    const url = this.url('/reports/ressourcekortlaegning/preview', {
      ppi: options.ppi,
      resource_template_id: options.templateId ?? undefined,
    });
    const response = await this.doFetch(url, { method: 'GET', signal });
    if (!response.ok) throw await apiErrorFor(response, url);
    return { status: response.status === 202 ? 202 : 200, preview: (await response.json()) as ReportPreview };
  }

  /** A preview's state by digest; 404 when superseded or evicted (ask again). */
  reportPreviewStatus(digest: string, ppi?: number, signal?: AbortSignal): Promise<ReportPreview> {
    return this.requestJson<ReportPreview>(
      `/reports/ressourcekortlaegning/preview/${encodeURIComponent(digest)}`,
      { ppi },
      signal,
    );
  }

  /** URL of one (0-based) preview page — immutable, so an `<img>` caches it for good. */
  reportPreviewPageUrl(digest: string, index: number, ppi?: number): string {
    return this.url(`/reports/ressourcekortlaegning/preview/${encodeURIComponent(digest)}/pages/${index}`, { ppi });
  }

  /** The report's cover choice; 404 on a ruxd from before it. */
  reportCover(signal?: AbortSignal): Promise<ReportCover> {
    return this.requestJson<ReportCover>('/reports/cover', undefined, signal);
  }

  /** Choose a photo or 360° panorama (+ region) as the cover. */
  setReportCover(view: ResourceView, signal?: AbortSignal): Promise<ReportCover> {
    return this.putJson<ReportCover>('/reports/cover', { kind: view.kind, id: view.id, region: view.region }, signal);
  }

  /** Back to the model render (`source: null`). */
  async clearReportCover(signal?: AbortSignal): Promise<ReportCover> {
    const url = this.url('/reports/cover');
    const response = await this.doFetch(url, { method: 'DELETE', signal });
    if (!response.ok) throw await apiErrorFor(response, url);
    return (await response.json()) as ReportCover;
  }

  /** Upload a JPEG or PNG (≤ 20 MB) as the cover. */
  async uploadReportCover(file: Blob, signal?: AbortSignal): Promise<ReportCover> {
    const url = this.url('/reports/cover/image');
    const response = await this.doFetch(url, {
      method: 'PUT',
      headers: { 'Content-Type': file.type || 'application/octet-stream' },
      body: file,
      signal,
    });
    if (!response.ok) throw await apiErrorFor(response, url);
    return (await response.json()) as ReportCover;
  }

  /** URL of the cover as the report embeds it (cropped JPEG, or the upload); `v` busts the cache after a change. */
  reportCoverImageUrl(v?: number | string): string {
    return this.url('/reports/cover/image', { v });
  }

  // -------------------------------------------------- exports / CSV ----

  /**
   * URL for a CSV download, optionally restricted to a column subset.
   *
   * Returns a URL string for use in `<a href>` — the browser streams the
   * response directly so we never buffer the CSV in JS.
   *
   * Pass `columns` to restrict output. Omit (or pass undefined / empty) for
   * all columns — the server default. Order matters: the output header follows
   * the order in `columns`.
   */
  csvExportUrl(columns?: string[]): string {
    return this.url(
      '/exports/csv',
      columns && columns.length > 0 ? { columns: columns.join(',') } : undefined,
    );
  }

  // -------------------------------------------------------- websocket ----

  /**
   * WebSocket URL for the event channel, derived from the API base.
   *
   * Resolved against `location` so a relative base (the normal case) produces
   * an absolute `ws://`/`wss://` URL — `new WebSocket()` requires one.
   */
  eventsUrl(origin?: { protocol: string; host: string }): string {
    const loc = origin ?? (typeof location !== 'undefined' ? location : undefined);
    if (!loc) return `${this.baseUrl}/events`;
    const scheme = loc.protocol === 'https:' ? 'wss:' : 'ws:';
    const path = this.baseUrl.startsWith('http')
      ? new URL(this.baseUrl).pathname
      : this.baseUrl;
    return `${scheme}//${loc.host}${path}/events`;
  }

  // --------------------------------------------------------- survey ----

  survey(signal?: AbortSignal): Promise<Survey> {
    return this.requestJson<Survey>('/survey', undefined, signal);
  }

  surveySummary(signal?: AbortSignal): Promise<SurveySummary> {
    return this.requestJson<SurveySummary>('/survey/summary', undefined, signal);
  }

  /** Photo count + best frame per instance-backed part, in one request. */
  surveyPhotos(signal?: AbortSignal): Promise<SurveyPhotos> {
    return this.requestJson<SurveyPhotos>('/survey/photos', undefined, signal);
  }

  surveyFractions(signal?: AbortSignal): Promise<SurveyFractions> {
    return this.requestJson<SurveyFractions>('/survey/fractions', undefined, signal);
  }

  /** Create parts for instances that have none. Idempotent; never overwrites edits. */
  syncSurvey(
    body: { instances_cloud?: string; semantic_cloud?: string; rooms_cloud?: string; measure?: boolean } = {},
  ): Promise<SurveySyncReport> {
    return this.postJson<SurveySyncReport>('/survey/sync', body);
  }

  /**
   * "Mål fra modellen" (`POST /survey/measure`, ReUseX schema v37): ground
   * the parts in the typed model's walls, openings and slabs. A server
   * before it answers 404; a project below v37 opened read-only, 422.
   */
  measureSurvey(body: { seed_elements?: boolean; suggest_buildups?: boolean } = {}): Promise<SurveyMeasureReport> {
    return this.postJson<SurveyMeasureReport>('/survey/measure', body);
  }

  /** The project's Opbygning library (`GET /buildups`). A server before v37 answers 404. */
  async buildups(signal?: AbortSignal): Promise<Buildup[]> {
    const body = await this.requestJson<{ buildups: Buildup[] }>('/buildups', undefined, signal);
    return body.buildups;
  }

  createSurveyType(body: SurveyTypeCreate): Promise<SurveyType> {
    return this.postJson<SurveyType>('/survey/types', body);
  }

  /** Sparse edit. `review_status: 'approved'` is refused with a 422 while a sample is pending. */
  patchSurveyType(id: number, patch: SurveyTypePatch): Promise<SurveyType> {
    return this.patchJson<SurveyType>(`/survey/types/${id}`, patch);
  }

  /** Delete a type and all its parts (scan-backed ones are tombstoned). */
  async deleteSurveyType(id: number): Promise<SurveyTypeDeletion> {
    const url = this.url(`/survey/types/${id}`);
    const response = await this.doFetch(url, { method: 'DELETE' });
    if (!response.ok) {
      throw await apiErrorFor(response, url);
    }
    return (await response.json()) as SurveyTypeDeletion;
  }

  patchSurveyPart(code: string, patch: SurveyPartPatch): Promise<SurveyPart> {
    return this.patchJson<SurveyPart>(`/survey/parts/${encodeURIComponent(code)}`, patch);
  }

  /**
   * One patch for several types in one transaction (`PATCH /survey/types`);
   * a refused batch changes nothing. A server before ruxd-6tz answers 404/405.
   */
  async patchSurveyTypes(body: SurveyTypeBatchPatch): Promise<SurveyType[]> {
    const out = await this.patchJson<{ types: SurveyType[] }>('/survey/types', body);
    return out.types;
  }

  /**
   * Merge type `id` into `into`: parts, sample links and mass move, `id` is
   * deleted, all server-side. A server before ruxd-6tz answers 404.
   */
  mergeSurveyType(id: number, into: number): Promise<SurveyTypeMergeResult> {
    return this.postJson<SurveyTypeMergeResult>(`/survey/types/${id}/merge`, { into });
  }

  /** Stored storeys, lowest first (`GET /storeys`). A server before ruxd-6tz answers 404. */
  async storeys(signal?: AbortSignal): Promise<Storey[]> {
    const body = await this.requestJson<{ storeys: Storey[] }>('/storeys', undefined, signal);
    return body.storeys;
  }

  /**
   * The building model's spaces in world coordinates (`GET /spaces`,
   * ruxd#11): polygon, storey, floor/ceiling heights. `[]` when none are
   * stored; a server before ruxd#11 answers 404.
   */
  async spaces(signal?: AbortSignal): Promise<SolidSpace[]> {
    const body = await this.requestJson<{ spaces: SolidSpace[] }>('/spaces', undefined, signal);
    return body.spaces;
  }

  /** The zones, named groups of spaces (`GET /zones`, ruxd #33). A server before it answers 404. */
  async zones(signal?: AbortSignal): Promise<Zone[]> {
    const body = await this.requestJson<{ zones: Zone[] }>('/zones', undefined, signal);
    return body.zones;
  }

  /** The stairs (`GET /stairs`, ruxd #35): runs, landings and the riser checks. A server before it answers 404. */
  async stairs(signal?: AbortSignal): Promise<Stair[]> {
    const body = await this.requestJson<{ stairs: Stair[] }>('/stairs', undefined, signal);
    return body.stairs;
  }

  /**
   * `POST /spaces/rebuild`: a storey's spaces from its walls, or with
   * `seed` the one room the walls close around a point. `preview` (the
   * default) writes nothing; applied it is one model edit. 422: the reason.
   */
  rebuildSpaces(request: SpacesRebuildRequest, signal?: AbortSignal): Promise<SpacesRebuildResult> {
    return this.postJson<SpacesRebuildResult>('/spaces/rebuild', request, signal);
  }

  /** `POST /spaces/merge`: b joined into a across the walls between them (preview or apply). */
  mergeSpaces(request: SpacesMergeRequest, signal?: AbortSignal): Promise<SpacesMergeResult> {
    return this.postJson<SpacesMergeResult>('/spaces/merge', request, signal);
  }

  /** `POST /spaces/split`: a room cut in two along a line (preview or apply). */
  splitSpace(request: SpacesSplitRequest, signal?: AbortSignal): Promise<SpacesSplitResult> {
    return this.postJson<SpacesSplitResult>('/spaces/split', request, signal);
  }

  /**
   * `POST /ceilings/fit` ("Tilpas til punktsky"): a ceiling plane fitted to
   * the cloud over a space (or a region). Writes nothing — send the plane
   * back with `ceiling.set`. A fit that finds too little is `ok: false`.
   */
  fitCeiling(request: CeilingFitRequest, signal?: AbortSignal): Promise<CeilingFitResult> {
    return this.postJson<CeilingFitResult>('/ceilings/fit', request, signal);
  }

  /** The building model's walls as world centre lines (`GET /walls`, ruxd#11). */
  async walls(signal?: AbortSignal): Promise<SolidWall[]> {
    const body = await this.requestJson<{ walls: SolidWall[] }>('/walls', undefined, signal);
    return body.walls;
  }

  /** The building model's doors, windows and openings (`GET /openings`, ruxd#11). */
  async openings(signal?: AbortSignal): Promise<SolidOpening[]> {
    const body = await this.requestJson<{ openings: SolidOpening[] }>('/openings', undefined, signal);
    return body.openings;
  }

  /**
   * The building model's floor slabs and roof pieces (`GET /slabs`,
   * ruxd-jmt): footprint polygon, storey, base/top heights. `[]` when the
   * solids stage has not run; a server before ruxd-jmt answers 404.
   */
  async slabs(signal?: AbortSignal): Promise<SolidSlab[]> {
    const body = await this.requestJson<{ slabs: SolidSlab[] }>('/slabs', undefined, signal);
    return body.slabs;
  }

  // ------------------------------------ solid-model edits (ruxd-5uv) ----
  // Every write needs editor/owner and answers 409 `case_busy` while a
  // pipeline job runs; a stale `base_revision` is 409 `model_conflict`.
  // Commits are announced with `model.changed`.

  /** `POST /model/edits`: apply a batch as one undo step (world coordinates, as the GETs serve them). */
  applyModelEdits(request: ModelEditRequest, signal?: AbortSignal): Promise<ModelEditResult> {
    return this.postJson<ModelEditResult>('/model/edits', request, signal);
  }

  /** `POST /model/undo`: undo the newest edit (409 `nothing_to_undo` / `model_conflict`). */
  undoModelEdit(request: ModelUndoRequest = {}, signal?: AbortSignal): Promise<ModelEditResult> {
    return this.postJson<ModelEditResult>('/model/undo', request, signal);
  }

  /** `POST /model/redo`: redo the oldest undone edit (409 `nothing_to_redo` / `model_conflict`). */
  redoModelEdit(request: ModelUndoRequest = {}, signal?: AbortSignal): Promise<ModelEditResult> {
    return this.postJson<ModelEditResult>('/model/redo', request, signal);
  }

  /** `GET /model/history`: the edit log, oldest first (`after` = only entries with a greater id). */
  modelHistory(after?: number, signal?: AbortSignal): Promise<ModelHistory> {
    return this.requestJson<ModelHistory>('/model/history', { after }, signal);
  }

  /** `GET /model/revision`: the revision to send as `base_revision`, undo / redo state, solids staleness. */
  modelRevision(signal?: AbortSignal): Promise<ModelRevision> {
    return this.requestJson<ModelRevision>('/model/revision', undefined, signal);
  }

  /** `GET /model/snap?storey=`: wall axes, endpoints, axis angles and plane traces on one storey. */
  modelSnap(storey: number, signal?: AbortSignal): Promise<ModelSnap> {
    return this.requestJson<ModelSnap>('/model/snap', { storey }, signal);
  }

  /** `POST /model/rederive`: queue a `solids` run so `/slabs` and `/elements` follow the edits (202, the job). */
  rederiveSolids(signal?: AbortSignal): Promise<Job> {
    return this.postJson<Job>('/model/rederive', {}, signal);
  }

  /**
   * The building model's elements, each with its own world-coordinate mesh
   * (`GET /elements`): walls, slabs, roof, spaces, doors, windows and other
   * openings. `[]` when the solids stage has not run; a server before the
   * route answers 404.
   */
  async elements(signal?: AbortSignal): Promise<BuildingElement[]> {
    const body = await this.requestJson<{ elements: BuildingElement[] }>('/elements', undefined, signal);
    return body.elements;
  }

  /** Rooms of the `rooms` cloud with centroid, box and storey (`GET /rooms`). A server before ruxd-6tz answers 404. */
  async rooms(signal?: AbortSignal): Promise<Room[]> {
    const body = await this.requestJson<{ rooms: Room[] }>('/rooms', undefined, signal);
    return body.rooms;
  }

  async samples(signal?: AbortSignal): Promise<Sample[]> {
    const body = await this.requestJson<{ samples: Sample[] }>('/samples', undefined, signal);
    return body.samples;
  }

  createSample(body: SampleCreate): Promise<Sample> {
    return this.postJson<Sample>('/samples', body);
  }

  patchSample(id: number, patch: SamplePatch): Promise<Sample> {
    return this.patchJson<Sample>(`/samples/${id}`, patch);
  }

  setSampleLinks(id: number, links: SampleLinksPut): Promise<Sample> {
    return this.putJson<Sample>(`/samples/${id}/links`, links);
  }

  async deleteSample(id: number): Promise<void> {
    const url = this.url(`/samples/${id}`);
    const response = await this.doFetch(url, { method: 'DELETE' });
    if (!response.ok) {
      throw await apiErrorFor(response, url);
    }
  }

  /** URL of a server-rendered evidence image, for use as an `<img src>`. */
  renderUrl(query: RenderQuery): string {
    return this.url('/renders', {
      view: query.view,
      orbit_index: query.orbit_index,
      highlight_instance: query.highlight_instance,
      highlight_cloud: query.highlight_cloud,
      layers: query.layers?.join(','),
      width: query.width,
      height: query.height,
    });
  }
}

/**
 * The client the app uses: same-origin, pointed at the open case by
 * `main.tsx` (`api.selectCase`) before anything renders.
 */
export const api = new RuxApiClient();
